import React, { useCallback, useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';
import { Play, RotateCcw } from 'lucide-react';

import { errorFromResponse, networkErrorMessage, readJson } from '../../portal/portalFetch';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/portal`;

const C = {
  bg: '#0f172a',
  card: '#111827',
  line: '#1e293b',
  text: '#e2e8f0',
  mute: '#94a3b8',
  green: '#86efac',
};

const SAMPLES = {
  python: {
    source: 'print("hello from python")\nprint(2 + 3)\n',
    stdin: '',
  },
  javascript: {
    source: 'console.log("hello from javascript");\nconsole.log(2 + 3);\n',
    stdin: '',
  },
  java: {
    source: `public class Main {
  public static void main(String[] args) {
    System.out.println("hello from java");
    System.out.println(2 + 3);
  }
}
`,
    stdin: '',
  },
  cpp: {
    source: `#include <iostream>
int main() {
  std::cout << "hello from c++" << std::endl;
  std::cout << (2 + 3) << std::endl;
  return 0;
}
`,
    stdin: '',
  },
};

const editorLang = (language) => (language === 'cpp' ? 'cpp' : language);

export default function CodeLab() {
  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState(SAMPLES.python.source);
  const [stdin, setStdin] = useState('');
  const [running, setRunning] = useState(false);
  const [runOutput, setRunOutput] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState({ ok: false, url: '', languages: [] });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/code-test/status`)
      .then((res) => readJson(res))
      .then((data) => {
        if (cancelled) return;
        setStatus({
          ok: Boolean(data.ok),
          url: data.url || '',
          languages: Array.isArray(data.languages) ? data.languages : [],
          error: data.error || '',
        });
        if (!data.ok) setError(data.error || 'Piston is not reachable. Start the code runner and set PISTON_URL.');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(networkErrorMessage(err, 'Could not reach the API.') || 'Could not reach the API.');
      });
    return () => { cancelled = true; };
  }, []);

  const loadSample = useCallback((lang) => {
    const sample = SAMPLES[lang] || SAMPLES.python;
    setLanguage(lang);
    setCode(sample.source);
    setStdin(sample.stdin);
    setRunOutput('');
  }, []);

  const runCode = async () => {
    setRunning(true);
    setError('');
    setRunOutput('Running…');
    try {
      const res = await fetch(`${API}/code-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language, source: code, stdin }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(errorFromResponse(data, 'Run failed.'));
      setRunOutput((data.output || data.error || '(no output)').trim());
      if (data.ok) setStatus((prev) => ({ ...prev, ok: true }));
    } catch (err) {
      const msg = networkErrorMessage(err, err.message) || err.message;
      setRunOutput(msg);
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  const card = {
    background: C.card,
    border: `1px solid ${C.line}`,
    borderRadius: 14,
    padding: 16,
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 20px 60px' }}>
        <div style={{ fontSize: 12, color: C.mute, letterSpacing: 0.7, textTransform: 'uppercase' }}>Recruiter24x7 · Code Lab</div>
        <h1 style={{ margin: '6px 0 8px', fontSize: 28 }}>Interview code runner</h1>
        <p style={{ color: C.mute, marginTop: 0, lineHeight: 1.6, maxWidth: 780 }}>
          Temporary test page. Same Piston runner as the interview coding task — no meeting token needed.
        </p>
        <div style={{ fontSize: 13, marginBottom: 16, color: status.ok ? C.green : '#fca5a5' }}>
          {status.ok
            ? `Piston ready${status.languages.length ? ` · ${status.languages.join(', ')}` : ''}`
            : 'Piston not reachable — start recruitai-piston and set PISTON_URL, then refresh.'}
        </div>
        {status.url && (
          <div style={{ fontSize: 12, color: C.mute, marginTop: -10, marginBottom: 16 }}>{status.url}</div>
        )}

        {error && !running && (
          <div style={{ ...card, borderColor: 'rgba(248,113,113,0.4)', color: '#fca5a5', marginBottom: 16 }}>{error}</div>
        )}

        <section style={card}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={language}
              onChange={(e) => loadSample(e.target.value)}
              style={{ background: '#0f172a', color: C.text, border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 8px' }}
            >
              <option value="python">Python</option>
              <option value="javascript">JavaScript</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
            </select>
            <button
              type="button"
              onClick={runCode}
              disabled={running || !code.trim()}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: 8, fontWeight: 700,
                background: '#065f46', color: '#fff', cursor: running || !code.trim() ? 'default' : 'pointer',
              }}
            >
              <Play size={12} style={{ verticalAlign: '-2px' }} /> {running ? 'Running…' : 'Run'}
            </button>
            <button
              type="button"
              onClick={() => loadSample(language)}
              style={{
                padding: '6px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
                background: '#0f172a', color: C.text, cursor: 'pointer',
              }}
            >
              <RotateCcw size={12} style={{ verticalAlign: '-2px' }} /> Reset sample
            </button>
          </div>

          <div style={{ height: 280, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden' }}>
            <Editor
              height="280px"
              theme="vs-dark"
              language={editorLang(language)}
              value={code}
              onChange={(v) => setCode(v || '')}
              options={{ minimap: { enabled: false }, fontSize: 13, automaticLayout: true }}
            />
          </div>
          <input
            value={stdin}
            onChange={(e) => setStdin(e.target.value)}
            placeholder="stdin (optional)"
            style={{
              width: '100%', marginTop: 8, boxSizing: 'border-box', borderRadius: 8,
              background: '#0f172a', color: C.text, border: `1px solid ${C.line}`, padding: '8px 10px', fontSize: 13,
            }}
          />
          {runOutput && (
            <pre style={{
              margin: '10px 0 0', padding: 12, borderRadius: 8, background: '#020617',
              color: '#86efac', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto',
            }}>{runOutput}</pre>
          )}
        </section>
      </div>
    </div>
  );
}
